import { Box, Button, HStack, Icon, Input, InputField, Typography } from "components/ui";
import { useLocalSearchParams, useSegments } from "expo-router";
import { Minus, Plus, ShoppingCart } from "lucide-react-native";
import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useGetCartCurrentQuery, usePutCartCurrentItemMutation } from "store/abcoApi";
import { useIsMobile } from "utils/hooks/useIsMobile";
import { useDataLayer } from "utils/hooks/useDataLayer";
import { useSiteSearch } from "utils/hooks/useSiteSearch";
import { reportAddSearchClick } from "utils/addSearchAnalytics";
import { Item } from "store/abcoApi";

type AddToCartProps = {
	stockcode: string | null | undefined;
	rechargeable?: boolean | null | undefined;
	isPurchaseable?: boolean | null | undefined;
	showNotPurchaseableMessage?: boolean | null | undefined;
	item: Item;
	isOnSearch?: boolean;
};

const AddToCart = React.memo(function AddToCart({ stockcode, rechargeable, isPurchaseable, showNotPurchaseableMessage = false, item, isOnSearch = false }: AddToCartProps) {
	const { data: cart } = useGetCartCurrentQuery();
	const [quantity, setQuantity] = useState<number>(0);
	const [quantityInCart, setQuantityInCart] = useState<number>(0);
	const [updateItem, { isLoading: isUpdating }] = usePutCartCurrentItemMutation();
	const isMobile = useIsMobile();

	const segments = useSegments() as string[];
	const searchParams = useLocalSearchParams();

	const isSitePage = segments.length >= 5 && segments[3] === "sites" && typeof segments[4] === "string";
	const siteId = isSitePage && typeof searchParams.id === "string" ? Number(searchParams.id) : undefined;

	// Memoize cart location check to prevent re-calculations
	const cartMatchesLocation = useMemo(() => {
		if (!cart || !siteId) return true;
		return cart.siteId === siteId;
	}, [cart?.siteId, siteId]);

	// Memoize the quantity in cart to prevent unnecessary updates
	const cartQuantity = useMemo(() => {
		if (!cartMatchesLocation) return 0;
		return cart?.lines?.find((i) => i.item?.stockcode === stockcode)?.quantity || 0;
	}, [cart?.lines, stockcode, cartMatchesLocation]);

	const { addToCart, removeFromCart } = useDataLayer();
	const { searchTerm } = useSiteSearch();

	// Use refs to store stable values and prevent recreating functions
	const quantityInCartRef = useRef(quantityInCart);
	const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
	const isUpdatingRef = useRef(false);

	// Update quantity when cart changes, but only if it actually changed
	useEffect(() => {
		if (cartQuantity !== quantityInCartRef.current) {
			setQuantityInCart(cartQuantity);
			setQuantity(cartQuantity);
			quantityInCartRef.current = cartQuantity;
		}
	}, [cartQuantity]);

	// Stable update function using refs to prevent recreation
	const updateCartStable = useCallback(async () => {
		const currentQuantity = quantity;
		const currentQuantityInCart = quantityInCartRef.current;

		if (!stockcode || currentQuantity === currentQuantityInCart || isUpdatingRef.current) {
			return;
		}

		isUpdatingRef.current = true;
		const isIncrease = currentQuantity > currentQuantityInCart;

		// Analytics tracking
		if (typeof window !== "undefined") {

			if (currentQuantity > 0) {
				if (isIncrease) {
					
					addToCart(item, quantity - quantityInCart)
				} else {
					removeFromCart(item, quantity - quantityInCart);
				}
			}
		}

		try {
			await updateItem({
				quantity: currentQuantity,
				stockcode,
				siteId,
				isRechargeable: rechargeable || undefined,
			}).unwrap();

			// Update refs to prevent unnecessary re-runs
			quantityInCartRef.current = currentQuantity;
			setQuantityInCart(currentQuantity);

			
		} catch (error) {
			console.error("Failed to update cart:", error);
			// Reset quantity on error
			setQuantity(currentQuantityInCart);
		} finally {
			isUpdatingRef.current = false;
		}
	}, [quantity, stockcode, siteId, rechargeable, updateItem, addToCart, removeFromCart]);

	// Debounced Update with cleanup
	useEffect(() => {
		if (debounceTimeout.current) {
			clearTimeout(debounceTimeout.current);
		}

		debounceTimeout.current = setTimeout(updateCartStable, 1200);

		return () => {
			if (debounceTimeout.current) {
				clearTimeout(debounceTimeout.current);
			}
		};
	}, [quantity, updateCartStable]);

	const updateQuantity = useCallback(
		(qty: string | number) => {
			if (searchTerm && searchTerm.length > 0 && isOnSearch) {
				reportAddSearchClick({
					keyword: searchTerm,
					docid: item.slug,
				});
			}

			const newQuantity = Math.max(0, parseInt(qty.toString()) || 0);
			setQuantity(newQuantity);
		},
		[searchTerm, isOnSearch, item.slug],
	);

	if (!stockcode) return null;

	if (quantityInCart === 0 && quantity === 0) {
		if (!isPurchaseable) {
			if (showNotPurchaseableMessage) {
				return (
					<Box>
						<Typography weight="normal">Please contact your supervisor to request this item.</Typography>
					</Box>
				);
			}
			return null;
		}

		if (isMobile) {
			return (
				<Box maxWidth={232}>
					<Button paddingHorizontal={16} gap={8} padding={12} bg="$secondary" borderRadius={16} onPress={() => updateQuantity(1)}>
						<Plus size={18} />
						<ShoppingCart />
					</Button>
				</Box>
			);
		}
		return (
			<Box maxWidth={232}>
				<Button gap={8} padding={12} bg="$secondary" borderRadius={16} onPress={() => updateQuantity(1)}>
					<ShoppingCart />
					<Typography weight="bold">Add to cart</Typography>
				</Button>
			</Box>
		);
	}

	return (
		<HStack bg="$basicwhite" padding={4} borderRadius={12} borderWidth={1} borderColor="$neutral200" justifyContent="center" alignItems="center" gap={isMobile ? 12 : 16}>
			{isUpdating && (
				<Box
					style={{
						position: "absolute",
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						backgroundColor: "rgba(255,255,255,0.5)",
						zIndex: 1,
						justifyContent: "center",
						alignItems: "center",
					}}
				/>
			)}
			<Button borderColor={"$neutral400"} borderRadius={10} p={0} width={36} aspectRatio={1} borderWidth={1} onPress={() => updateQuantity(quantity - 1)}>
				<Icon color="$primary" as={Minus} />
			</Button>
			{/* Input itself has no border – relies on HStack border */}
			<Input variant="outline" size="md" borderWidth={0} style={{ width: 50, height: 40 }}>
				<InputField
					key={"quantity"}
					fontSize={16}
					p={0}
					textAlign="center"
					fontWeight="$semibold"
					type="text"
					inputMode="numeric"
					value={quantity.toString()}
					onChangeText={updateQuantity}
					onBlur={() => {
						if (debounceTimeout.current) {
							clearTimeout(debounceTimeout.current);
						}
						updateCartStable();
					}}
					returnKeyType="done"
					keyboardType="numeric"
					autoComplete="off"
					readOnly={isUpdating}
					style={{ width: "100%" }}
				/>
			</Input>
			<Button borderColor={"$neutral400"} borderRadius={10} p={0} width={36} aspectRatio={1} borderWidth={1} onPress={() => updateQuantity(quantity + 1)}>
				<Icon color="$primary" as={Plus} />
			</Button>
		</HStack>
	);
});

export default AddToCart;
